import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, User as UserIcon } from "lucide-react";

export default function Profile() {
  const { user, refreshMe } = useAuth();
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [cf, setCf] = useState("");
  const [saving, setSaving] = useState(false);

  const changePw = async (e) => {
    e.preventDefault();
    if (nw.length < 6) return toast.error("New password must be 6+ characters");
    if (nw !== cf) return toast.error("New passwords do not match");
    if (nw === cur) return toast.error("New password must be different from current");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password changed. Please use the new password next time you log in.");
      setCur(""); setNw(""); setCf("");
      refreshMe();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
      <div className="mb-6">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[hsl(var(--brand-orange))]">Account</div>
        <h1 className="mt-1 font-display text-3xl font-bold">Your profile & security</h1>
      </div>

      <Card className="border-border/70">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[hsl(var(--brand-navy))] text-xl font-bold text-white">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div>
              <div className="font-display text-lg font-bold">{user?.name}</div>
              <div className="text-sm text-muted-foreground">{user?.email}</div>
              <div className="mt-2 flex gap-2 text-xs">
                <span className="rounded-full bg-secondary px-2 py-0.5">Role: <b className="capitalize">{user?.role}</b></span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mt-6 border-border/70">
        <CardContent className="p-6">
          <div className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-[hsl(var(--brand-orange))]" />
            <h2 className="font-display text-lg font-bold">Change password</h2>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">For security, enter your current password to change it.</p>
          <form onSubmit={changePw} className="mt-5 space-y-4">
            <div>
              <Label htmlFor="cur">Current password</Label>
              <Input id="cur" type="password" required value={cur} onChange={(e) => setCur(e.target.value)} data-testid="cur-password-input" className="mt-1.5 max-w-sm" />
            </div>
            <div>
              <Label htmlFor="nw">New password</Label>
              <Input id="nw" type="password" required minLength={6} value={nw} onChange={(e) => setNw(e.target.value)} data-testid="new-password-input" className="mt-1.5 max-w-sm" />
              <div className="mt-1 text-xs text-muted-foreground">Minimum 6 characters.</div>
            </div>
            <div>
              <Label htmlFor="cf">Confirm new password</Label>
              <Input id="cf" type="password" required minLength={6} value={cf} onChange={(e) => setCf(e.target.value)} data-testid="confirm-password-input" className="mt-1.5 max-w-sm" />
            </div>
            <Button type="submit" disabled={saving} className="bg-[hsl(var(--brand-orange))] hover:bg-[hsl(var(--brand-orange))]/90" data-testid="change-password-btn">
              {saving ? "Saving..." : "Update password"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
